/**
 * Lendwise email brand kit for every Resend email.
 *
 * IMPORT-FREE ON PURPOSE: Deno edge functions, Vite, Vitest and Node's type
 * stripping all load this file, and the catalog (notification-templates.ts)
 * plus the smaller email modules import it without creating a cycle. Keep it
 * free of imports, Deno globals, enums, namespaces and parameter properties.
 *
 * BRAND: the website's "Ledger" tokens (Frontfacing tailwind.config.js, pinned by
 * its tests/unit/design-tokens.test.ts) and Marketing's approved metallic
 * horizontal lockup on a forest masthead. Email clients cannot load the site's
 * Source Serif 4 / Geist: Gmail ignores @font-face and classic Outlook falls back
 * to Times New Roman, so Georgia stands in for the display serif and Arial for
 * the interface sans. Labels are sentence case at 12px or larger. Status is a
 * small labeled chip under the title; muted brick is reserved for critical
 * states (expired lock, required or overdue disclosures, failures).
 *
 * EMAIL-CLIENT RULES (caniemail): inline styles only (no <style>, @media or
 * url() backgrounds; Gmail drops a whole style attribute that carries url()),
 * tables for layout, padding only on table cells (the button anchor also sets
 * mso-padding-alt), max-width only on tables inside an Outlook ghost table,
 * every <img> with explicit width AND height, border-collapse: separate wherever
 * a radius matters.
 *
 * The masthead, gold rule, loan-officer row and standard footer are lifted
 * byte-for-byte from the v5 review request (2026-09-23);
 * email-brand-golden.test.ts proves the review still renders identically.
 */

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export const BRAND = {
  forest: '#0E3A2B',
  gold: '#D4A01F',
  paper: '#FAF7F2',
  cream: '#F5EAD0',
  ink: '#15201A',
  goldLeaf: '#8B6E3F',
  rule: '#E8E2D4',
  muted: '#56625B',
  bodyText: '#33403A',
  badgeBorder: '#E6D3A4',
  white: '#FFFFFF',
} as const;

export const BRAND_FONT = {
  serif: "Georgia, 'Times New Roman', Times, serif",
  sans: 'Arial, Helvetica, sans-serif',
  mono: "'Courier New', Courier, monospace",
} as const;

// Approved metallic horizontal lockup hosted by Frontfacing — the same file
// company_profiles.logo_light_url points at. 800x241 source shown at 168x51.
// Byte-identical to _shared/assets/lendwise-horizontal-lockup-metallic-v1.png.
export const BRAND_LOCKUP = {
  url: 'https://lendwisemtg.com/assets/brand/lendwise-horizontal-lockup-metallic-v1.png',
  width: 168,
  height: 51,
  alt: 'Lendwise Mortgage',
  sha256: 'ab2233220dd50a820520d6f8ad1937a5abe7d33aad2cf90cbc5c3cfd48e3eb0b',
} as const;

export const BRAND_COMPANY = {
  name: 'Lendwise Mortgage',
  /** The legal entity, shown in every email footer (Anthony, 2026-09-26). */
  legalName: 'Lendwise Mortgage, Inc.',
  nmls: '2702455',
  address: '21800 Oxnard St Suite 220, Woodland Hills, CA 91367',
  servicingEmail: 'servicing@lendwisemtg.com',
} as const;

// House illustration: shown in the loan-officer row only when there is no headshot.
export const BRAND_HOUSE_ART_URL =
  'https://tpwubhqakasyvijikbmv.supabase.co/storage/v1/object/public/documents/marketing/lendwise-review-house.png';

export type StatusTone = 'success' | 'info' | 'attention' | 'neutral' | 'critical';

/**
 * success   done: approved, funded, received, paid (forest tint)
 * info      in motion: submitted, sent, assigned (forest outline)
 * attention someone must act: pending, reminders, expiring (cream + gold)
 * neutral   FYI (paper)
 * critical  ONLY expired lock, required/overdue disclosures, failures, lock data mismatches (muted brick)
 * Chip text passes WCAG AA on its fill at 12px bold (email-brand.test.ts computes it).
 */
export const STATUS_TONES: Record<StatusTone, {
  fill: string;
  border: string;
  text: string;
  calloutFill: string;
  calloutRule: string;
}> = {
  success: { fill: '#E6EFE9', border: '#B7CCBE', text: '#0E3A2B', calloutFill: '#E6EFE9', calloutRule: '#0E3A2B' },
  info: { fill: '#FFFFFF', border: '#0E3A2B', text: '#0E3A2B', calloutFill: '#FAF7F2', calloutRule: '#0E3A2B' },
  attention: { fill: '#F5EAD0', border: '#E6D3A4', text: '#6B4E16', calloutFill: '#F5EAD0', calloutRule: '#D4A01F' },
  neutral: { fill: '#FAF7F2', border: '#E8E2D4', text: '#56625B', calloutFill: '#FAF7F2', calloutRule: '#E8E2D4' },
  critical: { fill: '#F7E9E5', border: '#E3C2B8', text: '#8C3A2B', calloutFill: '#F7E9E5', calloutRule: '#8C3A2B' },
};

/** Every color a branded email may use. The contract test rejects anything else. */
export const BRAND_PALETTE: ReadonlySet<string> = new Set([
  ...Object.values(BRAND),
  ...Object.values(STATUS_TONES).flatMap((tone) => [tone.fill, tone.border, tone.text, tone.calloutFill, tone.calloutRule]),
].map((hex) => hex.toUpperCase()));

export const BRAND_WIDTH = { standard: 600, digest: 680 } as const;

// ---------------------------------------------------------------------------
// Escaping and links
// ---------------------------------------------------------------------------

/** HTML-escapes text for element or double-quoted attribute context. */
export const escapeHtml = (val: unknown): string => {
  const str = val == null ? '' : String(val);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Accepts http(s) URLs and bare host paths (normalized to https://); rejects
// javascript:, data:, mailto: and everything else.
const SAFE_URL_ATTR_RE = /^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}([/?#].*)?$/i;
const URL_SCHEME_RE = /^[a-z]+:\/\//i;

/** The safe normalized web URL (raw, NOT escaped), or null when unsafe. */
export const normalizeWebUrl = (val: unknown): string | null => {
  if (val == null) return null;
  const raw = String(val).trim();
  if (!raw || !SAFE_URL_ATTR_RE.test(raw)) return null;
  return URL_SCHEME_RE.test(raw) ? raw : `https://${raw}`;
};

/** The safe web URL escaped for an href/src attribute, or null when unsafe. */
export const sanitizeWebUrlAttr = (val: unknown): string | null => {
  const normalized = normalizeWebUrl(val);
  return normalized === null ? null : escapeHtml(normalized);
};

const EMAIL_RE = /^[^\s@<>"'`]+@[^\s@<>"'`]+\.[^\s@<>"'`]+$/;

/**
 * mailto: target for an address, or null. The address is URL-encoded
 * (HTML escaping is not enough: '+' is URL-significant); the encoded form is
 * attribute-safe as-is.
 */
export function mailtoHref(email: unknown): string | null {
  const raw = String(email ?? '').trim();
  if (!raw || !EMAIL_RE.test(raw)) return null;
  return `mailto:${encodeURIComponent(raw)}`;
}

/** tel: target (digits and a leading +), or null when too short to dial. */
export function telHref(phone: unknown): string | null {
  const digits = String(phone ?? '').replace(/[^0-9+]/g, '');
  if (digits.replace(/\+/g, '').length < 7) return null;
  return `tel:${encodeURIComponent(digits)}`;
}

/**
 * Plain-text header value: control characters (CR/LF included) collapse to
 * spaces so a data value can never inject a header break. Subjects are never
 * HTML-escaped.
 */
export function oneLine(value: unknown, fallback = ''): string {
  const flat = String(value ?? '').replace(/[\u0000-\u001F\u007F]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
  return flat || fallback;
}

/**
 * A header-safe subject that keeps its exact spacing: each run of control
 * characters (CR/LF included) becomes one space and nothing else changes, so a
 * subject built from ordinary data is returned unchanged.
 */
export function headerText(value: unknown): string {
  return String(value ?? '').replace(/[\u0000-\u001F\u007F]+/g, ' ');
}

// ---------------------------------------------------------------------------
// Html values: markup that is already safe. Plain strings are always escaped.
// ---------------------------------------------------------------------------

export interface Html {
  readonly __html: string;
}

export type HtmlValue = Html | string | number | null | undefined | false | readonly HtmlValue[];

/** Marks template-authored markup as safe. Never pass data through this. */
export function trusted(markup: string): Html {
  return { __html: markup };
}

function isHtml(value: unknown): value is Html {
  return typeof value === 'object' && value !== null && typeof (value as { __html?: unknown }).__html === 'string';
}

/** Renders a value: Html passes through, arrays join, everything else is escaped. */
export function renderHtml(value: HtmlValue): string {
  if (value === null || value === undefined || value === false) return '';
  if (Array.isArray(value)) return value.map((v) => renderHtml(v as HtmlValue)).join('');
  if (isHtml(value)) return value.__html;
  return escapeHtml(value);
}

/** Tagged template: interpolated values are escaped unless they are Html. */
export function html(strings: TemplateStringsArray, ...values: HtmlValue[]): Html {
  let out = strings[0];
  for (let i = 0; i < values.length; i += 1) {
    out += renderHtml(values[i]) + strings[i + 1];
  }
  return { __html: out };
}

export function joinHtml(parts: readonly HtmlValue[], separator: HtmlValue = ''): Html {
  const rendered = parts.map((part) => renderHtml(part)).filter((part) => part !== '');
  return { __html: rendered.join(renderHtml(separator)) };
}

/** Escaped text with line breaks kept as <br>. */
export function textWithBreaks(value: unknown): Html {
  return { __html: escapeHtml(value).replace(/\r?\n/g, '<br>') };
}

// ---------------------------------------------------------------------------
// Pieces lifted byte-for-byte from the v5 review request
// ---------------------------------------------------------------------------

/** Forest masthead with the approved lockup. With images off, the alt text shows in cream serif. */
export function mastheadRowHtml(): string {
  return `
        <tr>
          <td align="center" bgcolor="${BRAND.forest}" style="background: ${BRAND.forest}; padding: 12px 20px 11px;">
            <img src="${BRAND_LOCKUP.url}" alt="Lendwise Mortgage" width="168" height="51" style="display: block; width: 168px; height: 51px; border: 0; margin: 0 auto; color: ${BRAND.cream}; font-family: ${BRAND_FONT.serif}; font-size: 20px; line-height: 51px; text-align: center;" />
          </td>
        </tr>`;
}

/**
 * Loan-officer row: headshot (or the house illustration), name, role, email.
 * All string args must be pre-escaped by the caller for their context;
 * loEmailHref is the URL-encoded address. Use loFromRow() for raw values.
 * An officer with no email gets the row without the email line.
 */
export function loFromRowHtml(args: {
  photoUrl: string | null;
  loFull: string;
  loEmailHref: string;
  loEmail: string;
  roleLabel: string;
}): string {
  const visual = args.photoUrl
    ? `<img src="${args.photoUrl}" alt="${args.loFull}" width="72" height="72" style="display: block; width: 72px; height: 72px; border-radius: 50%; border: 2px solid ${BRAND.gold};" />`
    : `<img src="${BRAND_HOUSE_ART_URL}" alt="" width="84" height="76" style="display: block; width: 84px; height: 76px; border: 0;" />`;
  return `
            <table cellpadding="0" cellspacing="0" border="0" role="presentation" align="center" style="border-collapse: collapse; margin: 0 auto;">
              <tr>
                <td valign="middle" style="padding-right: 14px;">${visual}</td>
                <td valign="middle" align="left" style="text-align: left;">
                  <p style="font-family: ${BRAND_FONT.sans}; font-size: 16px; line-height: 20px; font-weight: bold; color: ${BRAND.ink}; margin: 0;">${args.loFull}</p>
                  <p style="font-family: ${BRAND_FONT.sans}; font-size: 13px; line-height: 18px; color: ${BRAND.muted}; margin: 2px 0 0;">${args.roleLabel}</p>${args.loEmail ? `
                  <p style="font-family: ${BRAND_FONT.sans}; font-size: 13px; line-height: 18px; margin: 1px 0 0;"><a href="mailto:${args.loEmailHref}" style="color: ${BRAND.forest}; text-decoration: underline;">${args.loEmail}</a></p>` : ''}
                </td>
              </tr>
            </table>`;
}

/** The website's GoldRule: one short gold line between who it's from and what it says. */
export function goldRuleHtml(): string {
  return `
            <table cellpadding="0" cellspacing="0" border="0" role="presentation" align="center" style="border-collapse: collapse; margin: 18px auto 16px;">
              <tr><td width="64" height="2" bgcolor="${BRAND.gold}" style="width: 64px; height: 2px; background: ${BRAND.gold}; font-size: 0; line-height: 2px; mso-line-height-rule: exactly;">&nbsp;</td></tr>
            </table>`;
}

/**
 * Standard footer row: year, company, NMLS and address. Extra lines (a talent
 * notice, a RESPA sentence, an unsubscribe link) follow on their own lines.
 */
export function footerRowHtml(extraLines: readonly HtmlValue[] = []): string {
  const extra = extraLines.map((line) => renderHtml(line)).filter(Boolean).map((line) => `<br />${line}`).join('');
  return `
        <tr>
          <td align="center" bgcolor="${BRAND.paper}" style="background: ${BRAND.paper}; border-top: 1px solid ${BRAND.rule}; padding: 18px 24px 20px; font-family: ${BRAND_FONT.sans}; font-size: 12px; line-height: 19px; color: ${BRAND.muted}; text-align: center;">
            &copy; ${new Date().getFullYear()} ${BRAND_COMPANY.legalName}, NMLS #${BRAND_COMPANY.nmls}<br />${BRAND_COMPANY.address}${extra}
          </td>
        </tr>`;
}

// ---------------------------------------------------------------------------
// Components (compact inline CSS keeps large emails under Gmail's clip limit)
// ---------------------------------------------------------------------------

// Font stacks go into style attributes verbatim; their quotes must not be entity-escaped.
const SANS = trusted(BRAND_FONT.sans);
const SERIF = trusted(BRAND_FONT.serif);
const MONO = trusted(BRAND_FONT.mono);

/** Hidden inbox preview line. A span (not a div) so no preview parser mistakes it for body copy. */
export function preheader(text: string): Html {
  const filler = '&#8204;&nbsp;'.repeat(60);
  return html`<span style="display:none;max-height:0;max-width:0;overflow:hidden;opacity:0;mso-hide:all;font-size:1px;line-height:1px;color:${BRAND.paper};">${text}${trusted(filler)}</span>`;
}

export interface StatusChip {
  tone: StatusTone;
  label: string;
  /** Gold 2px ring for last-day and overdue attention. */
  strong?: boolean;
}

export function chip(status: StatusChip, align: 'left' | 'center' = 'left'): Html {
  const tone = STATUS_TONES[status.tone];
  const border = status.strong ? `2px solid ${BRAND.gold}` : `1px solid ${tone.border}`;
  const padding = status.strong ? '3px 11px' : '4px 12px';
  const alignAttr = align === 'center' ? trusted(' align="center"') : '';
  const margin = align === 'center' ? '12px auto 0' : '12px 0 0';
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0"${alignAttr} style="border-collapse:separate;border-spacing:0;margin:${margin};"><tr><td bgcolor="${tone.fill}" style="background:${tone.fill};border:${border};border-radius:999px;padding:${padding};font-family:${SANS};font-size:12px;line-height:16px;font-weight:bold;color:${tone.text};mso-line-height-rule:exactly;">${status.label}</td></tr></table>`;
}

/** Several chips on one line (category plus timing, for example). */
export function chipRow(statuses: readonly StatusChip[], margin = '10px 0 0'): Html {
  if (statuses.length === 0) return trusted('');
  const cells = statuses.map((status, index) => {
    const tone = STATUS_TONES[status.tone];
    const border = status.strong ? `2px solid ${BRAND.gold}` : `1px solid ${tone.border}`;
    const padding = status.strong ? '3px 11px' : '4px 12px';
    const gap = index === statuses.length - 1 ? '' : html`<td width="6" style="width:6px;font-size:0;line-height:0;">&nbsp;</td>`;
    return html`<td bgcolor="${tone.fill}" style="background:${tone.fill};border:${border};border-radius:999px;padding:${padding};font-family:${SANS};font-size:12px;line-height:16px;font-weight:bold;color:${tone.text};mso-line-height-rule:exactly;white-space:nowrap;">${status.label}</td>${gap}`;
  });
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;border-spacing:0;margin:${margin};"><tr>${cells}</tr></table>`;
}

/** A smaller heading inside a section (one system in a credential list, one group of conditions). */
export function subheading(text: HtmlValue, margin = '20px 0 6px'): Html {
  return html`<h3 style="margin:${margin};font-family:${SANS};font-size:16px;line-height:22px;font-weight:bold;color:${BRAND.ink};">${text}</h3>`;
}

/** Closing lines: "Thank you," then a name, each on its own line. */
export function signOff(lines: readonly HtmlValue[]): Html {
  const kept = lines.filter((line) => renderHtml(line) !== '');
  if (kept.length === 0) return trusted('');
  return html`<p style="margin:20px 0 0;font-family:${SANS};font-size:15px;line-height:23px;color:${BRAND.ink};">${joinHtml(kept, trusted('<br>'))}</p>`;
}

export function paragraph(content: HtmlValue, opts: { size?: 'body' | 'lead'; align?: 'left' | 'center'; margin?: string } = {}): Html {
  const size = opts.size === 'lead' ? 'font-size:16px;line-height:26px;' : 'font-size:15px;line-height:24px;';
  const align = opts.align === 'center' ? 'text-align:center;' : '';
  return html`<p style="margin:${opts.margin ?? '0 0 16px'};font-family:${SANS};${trusted(size)}color:${BRAND.bodyText};${trusted(align)}">${content}</p>`;
}

/**
 * The editable body slot: the first element of the body cell. The Company
 * Settings preview reads its text as the template's default body.
 */
export function bodySlot(content: HtmlValue, size: 'body' | 'lead' = 'body'): Html {
  const type = size === 'lead' ? 'font-size:16px;line-height:26px;' : 'font-size:15px;line-height:24px;';
  return html`<p data-lw-slot="body" style="margin:0 0 18px;font-family:${SANS};${trusted(type)}color:${BRAND.bodyText};">${content}</p>`;
}

export function note(content: HtmlValue, align: 'left' | 'center' = 'left'): Html {
  const alignCss = align === 'center' ? 'text-align:center;' : '';
  return html`<p style="margin:0 0 12px;font-family:${SANS};font-size:13px;line-height:20px;color:${BRAND.muted};${trusted(alignCss)}">${content}</p>`;
}

export function sectionHeading(text: HtmlValue, opts: { margin?: string } = {}): Html {
  return html`<h2 style="margin:${opts.margin ?? '26px 0 10px'};font-family:${SERIF};font-size:19px;line-height:25px;font-weight:normal;color:${BRAND.ink};">${text}</h2>`;
}

export function bulletList(items: readonly HtmlValue[], margin = '0 0 16px'): Html {
  const rows = items
    .filter((item) => renderHtml(item) !== '')
    .map((item) => html`<tr><td valign="top" width="18" style="width:18px;padding:0 0 8px;font-family:${SANS};font-size:15px;line-height:24px;color:${BRAND.goldLeaf};">&#8226;</td><td valign="top" style="padding:0 0 8px;font-family:${SANS};font-size:15px;line-height:24px;color:${BRAND.bodyText};">${item}</td></tr>`);
  if (rows.length === 0) return trusted('');
  return html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:${margin};">${rows}</table>`;
}

export interface DetailRow {
  label: string;
  value: HtmlValue;
  /** Tints the whole row in this tone (a critical fact, for example). */
  tone?: StatusTone;
}

/** Label/value facts. Row headers are <th scope="row">; long values wrap. */
export function detailsTable(rows: ReadonlyArray<DetailRow | null | false | undefined>, opts: { ariaLabel?: string; margin?: string } = {}): Html {
  const kept = rows.filter((row): row is DetailRow => Boolean(row));
  if (kept.length === 0) return trusted('');
  const body = kept.map((row, index) => {
    const last = index === kept.length - 1;
    const rule = last ? '' : `border-bottom:1px solid ${BRAND.rule};`;
    if (row.tone) {
      // The tint needs inset padding on both cells so text never touches its edge.
      const tone = STATUS_TONES[row.tone];
      return html`<tr><th scope="row" align="left" valign="top" width="38%" bgcolor="${tone.fill}" style="width:38%;padding:9px 12px;background:${tone.fill};${trusted(rule)}font-family:${SANS};font-size:13px;line-height:20px;font-weight:bold;color:${tone.text};text-align:left;">${row.label}</th><td valign="top" bgcolor="${tone.fill}" style="padding:9px 12px;background:${tone.fill};${trusted(rule)}font-family:${SANS};font-size:15px;line-height:22px;font-weight:bold;color:${tone.text};word-break:break-word;overflow-wrap:anywhere;">${row.value}</td></tr>`;
    }
    return html`<tr><th scope="row" align="left" valign="top" width="38%" style="width:38%;padding:9px 12px 9px 0;${trusted(rule)}font-family:${SANS};font-size:13px;line-height:20px;font-weight:normal;color:${BRAND.muted};text-align:left;">${row.label}</th><td valign="top" style="padding:9px 0;${trusted(rule)}font-family:${SANS};font-size:15px;line-height:22px;color:${BRAND.ink};word-break:break-word;overflow-wrap:anywhere;">${row.value}</td></tr>`;
  });
  const aria = opts.ariaLabel ? html` aria-label="${opts.ariaLabel}"` : '';
  return html`<table width="100%" cellpadding="0" cellspacing="0" border="0"${aria} style="border-collapse:collapse;margin:${opts.margin ?? '0 0 20px'};border-top:1px solid ${BRAND.rule};border-bottom:1px solid ${BRAND.rule};">${body}</table>`;
}

export type CalloutTone = StatusTone | 'legal';

/** Tinted note with a left rule. 'legal' is paper with a gold-leaf rule, for verbatim compliance copy. */
export function callout(input: { tone: CalloutTone; title?: HtmlValue; body: HtmlValue }): Html {
  const fill = input.tone === 'legal' ? BRAND.paper : STATUS_TONES[input.tone].calloutFill;
  const rule = input.tone === 'legal' ? BRAND.goldLeaf : STATUS_TONES[input.tone].calloutRule;
  const title = input.title
    ? html`<p style="margin:0 0 6px;font-family:${SANS};font-size:15px;line-height:22px;font-weight:bold;color:${BRAND.ink};">${input.title}</p>`
    : '';
  return html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;margin:0 0 20px;"><tr><td bgcolor="${fill}" style="background:${fill};border-left:3px solid ${rule};padding:14px 16px;font-family:${SANS};font-size:14px;line-height:22px;color:${BRAND.bodyText};">${title}${input.body}</td></tr></table>`;
}

export interface DataColumn {
  label: string;
  align?: 'left' | 'right' | 'center';
}

/**
 * One table row: its cells, cells with a tint (a changed value, for example),
 * or one full-width cell that follows the row it annotates.
 */
export type DataRow =
  | ReadonlyArray<HtmlValue>
  | { readonly cells: ReadonlyArray<HtmlValue>; readonly fill?: string | null }
  | { readonly full: HtmlValue; readonly fill?: string | null };

/**
 * A data table: column headers are <th scope="col">, the table carries an
 * accessible name, and the wrapper scrolls sideways inside its own frame on a
 * narrow screen instead of widening the page. minWidth keeps many columns readable.
 */
export function dataTable(input: {
  ariaLabel: string;
  columns: readonly DataColumn[];
  rows: ReadonlyArray<DataRow>;
  minWidth?: number;
  margin?: string;
}): Html {
  const width = input.minWidth ? `min-width:${input.minWidth}px;` : '';
  const head = input.columns.map((column) => {
    const align = column.align ?? 'left';
    return html`<th scope="col" align="${align}" valign="bottom" style="padding:8px;background:${BRAND.paper};border-bottom:1px solid ${BRAND.rule};font-family:${SANS};font-size:12px;line-height:16px;font-weight:bold;color:${BRAND.muted};text-align:${align};">${column.label}</th>`;
  });
  const body = input.rows.map((row, rowIndex) => {
    const rule = rowIndex === input.rows.length - 1 ? '' : `border-bottom:1px solid ${BRAND.rule};`;
    const fill = !Array.isArray(row) && 'fill' in row && row.fill ? `background:${row.fill};` : '';
    if (!Array.isArray(row) && 'full' in row) {
      return html`<tr><td colspan="${input.columns.length}" style="padding:8px;${trusted(fill)}${trusted(rule)}font-family:${SANS};font-size:13px;line-height:19px;color:${BRAND.bodyText};">${row.full}</td></tr>`;
    }
    const cells = (Array.isArray(row) ? row : (row as { cells: ReadonlyArray<HtmlValue> }).cells).map((cell, index) => {
      const align = input.columns[index]?.align ?? 'left';
      return html`<td align="${align}" valign="top" style="padding:8px;${trusted(fill)}${trusted(rule)}font-family:${SANS};font-size:13px;line-height:19px;color:${BRAND.ink};text-align:${align};vertical-align:top;">${cell}</td>`;
    });
    return html`<tr>${cells}</tr>`;
  });
  return html`<div style="overflow-x:auto;margin:${input.margin ?? '0 0 20px'};"><table aria-label="${input.ariaLabel}" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;width:100%;${trusted(width)}border:1px solid ${BRAND.rule};"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
}

/**
 * Most rows a digest table shows by default. A lock-digest row costs about
 * 1.8 KB, so 35 rows keep that digest near 72 KB, under Gmail's ~102 KB clip;
 * tables with heavier rows pass a lower cap. The rest are summarized in a line
 * that points to the full list. Totals and plain-text parts still count every row.
 */
export const DIGEST_ROW_CAP = 35;

/** The first `cap` items of a digest list and how many were left out. */
export function capDigestRows<T>(items: readonly T[], cap: number = DIGEST_ROW_CAP): { shown: T[]; hidden: number } {
  const shown = items.slice(0, Math.max(0, cap));
  return { shown, hidden: items.length - shown.length };
}

/** "And 5 more loans. Open Loan Center to review them." under a capped digest table. */
export function moreRowsNote(
  hidden: number,
  noun: { one: string; many: string },
  link: { href: unknown; label: string; after: string },
): Html {
  if (hidden <= 0) return trusted('');
  return note(html`And ${hidden} more ${hidden === 1 ? noun.one : noun.many}. ${webLink(link.href, link.label)} ${link.after}`);
}

/** Text that must stay on one line (money, dates, loan numbers). */
export function nowrap(value: HtmlValue): Html {
  return html`<span style="white-space:nowrap;">${value}</span>`;
}

export interface StatTile {
  label: string;
  value: HtmlValue;
  tone?: StatusTone;
}

/**
 * Headline counts for a digest. Each tile is an inline block with a max width,
 * so tiles sit side by side at 680px and stack on a phone without media
 * queries; Outlook gets a ghost table row. Only the gaps between tiles carry a
 * margin: three 196px tiles and two 12px gaps fit the 616px digest row, and a
 * trailing margin on the last tile would push it onto a row of its own.
 */
export function statTiles(tiles: readonly StatTile[]): Html {
  if (tiles.length === 0) return trusted('');
  const cells = tiles.map((tile, index) => {
    const tone = STATUS_TONES[tile.tone ?? 'neutral'];
    const last = index === tiles.length - 1;
    const gap = last ? '' : '<!--[if mso]></td><td width="12"></td><td width="196" valign="top"><![endif]-->';
    const margin = last ? '0 0 12px' : '0 12px 12px 0';
    return html`<div style="display:inline-block;width:100%;max-width:196px;vertical-align:top;margin:${margin};"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;"><tr><td bgcolor="${tone.fill}" style="background:${tone.fill};border:1px solid ${tone.border};border-radius:8px;padding:12px 14px;"><p style="margin:0;font-family:${SANS};font-size:13px;line-height:18px;color:${tone.text};">${tile.label}</p><p style="margin:4px 0 0;font-family:${SERIF};font-size:26px;line-height:30px;color:${BRAND.ink};">${tile.value}</p></td></tr></table></div>${trusted(gap)}`;
  });
  return html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:0 0 8px;"><tr><td style="font-size:0;line-height:0;"><!--[if mso]><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td width="196" valign="top"><![endif]-->${cells}<!--[if mso]></td></tr></table><![endif]--></td></tr></table>`;
}

/** White bordered box for a repeated item (a condition, a document). */
export function panel(content: HtmlValue): Html {
  return html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;margin:0 0 12px;"><tr><td style="border:1px solid ${BRAND.rule};border-radius:8px;padding:14px 16px;font-family:${SANS};font-size:15px;line-height:23px;color:${BRAND.bodyText};">${content}</td></tr></table>`;
}

/** Forest button with a gold edge and live text. Renders nothing when the link is unsafe. */
export function button(input: { href: unknown; label: string; align?: 'left' | 'center' }): Html {
  const href = normalizeWebUrl(input.href);
  if (!href) return trusted('');
  const align = input.align ?? 'center';
  const alignAttr = align === 'center' ? trusted(' align="center"') : '';
  const margin = align === 'center' ? '6px auto 22px' : '6px 0 22px';
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0"${alignAttr} style="border-collapse:separate;margin:${margin};"><tr><td align="center" bgcolor="${BRAND.forest}" style="background:${BRAND.forest};border:2px solid ${BRAND.gold};border-radius:26px;mso-padding-alt:15px 34px;"><a href="${href}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:15px 34px;font-family:${SANS};font-size:16px;line-height:18px;font-weight:bold;color:${BRAND.white};text-decoration:none;border-radius:26px;">${input.label}</a></td></tr></table>`;
}

/** Inline forest link. Falls back to plain text when the target is unsafe. */
export function textLink(href: string | null, label: HtmlValue): Html {
  if (!href) return html`${label}`;
  return html`<a href="${href}" style="color:${BRAND.forest};text-decoration:underline;">${label}</a>`;
}

/** A web link: validated, escaped once, with the URL itself as the label by default. */
export function webLink(url: unknown, label?: HtmlValue): Html {
  const href = normalizeWebUrl(url);
  return textLink(href, label ?? href ?? '');
}

export function emailLink(email: unknown): Html {
  const raw = String(email ?? '').trim();
  return textLink(mailtoHref(raw), raw);
}

export function phoneLink(phone: unknown): Html {
  const raw = String(phone ?? '').trim();
  return textLink(telHref(raw), raw);
}

/** Monospace value for codes, user IDs and temporary passwords. */
export function codeText(value: unknown, opts: { highlight?: boolean } = {}): Html {
  const fill = opts.highlight ? BRAND.cream : BRAND.paper;
  const border = opts.highlight ? BRAND.badgeBorder : BRAND.rule;
  return html`<span style="font-family:${MONO};font-size:14px;line-height:22px;color:${BRAND.ink};background:${fill};border:1px solid ${border};padding:2px 6px;word-break:break-all;">${String(value ?? '')}</span>`;
}

/** A headline figure (amount due, amount received). */
export function figure(input: { label: string; value: string }): Html {
  return html`<p style="margin:18px 0 0;font-family:${SANS};font-size:13px;line-height:18px;color:${BRAND.muted};text-align:center;">${input.label}</p><p style="margin:2px 0 0;font-family:${SERIF};font-size:34px;line-height:40px;color:${BRAND.forest};text-align:center;">${input.value}</p>`;
}

export interface LoanOfficerContact {
  name: string;
  title?: string | null;
  nmls?: string | null;
  email?: string | null;
  phone?: string | null;
  photoUrl?: unknown;
}

/** "Questions? Contact your loan officer" card: photo, name, title/NMLS, email, phone. */
export function loCard(lo: LoanOfficerContact, heading = 'Questions? Contact your loan officer'): Html {
  const photo = sanitizeWebUrlAttr(lo.photoUrl);
  const photoCell = photo
    ? html`<td valign="top" width="72" style="width:72px;padding:0 14px 0 0;"><img src="${trusted(photo)}" alt="${lo.name}" width="56" height="56" style="display:block;width:56px;height:56px;border-radius:50%;border:2px solid ${BRAND.gold};" /></td>`
    : '';
  const sub = [lo.title, lo.nmls ? `NMLS #${lo.nmls}` : null].filter((v): v is string => Boolean(v && String(v).trim()));
  const line = (content: HtmlValue) => html`<p style="margin:2px 0 0;font-family:${SANS};font-size:14px;line-height:21px;color:${BRAND.bodyText};">${content}</p>`;
  return html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:8px 0 16px;border-top:1px solid ${BRAND.rule};"><tr><td style="padding:20px 0 0;"><p style="margin:0 0 12px;font-family:${SANS};font-size:13px;line-height:18px;color:${BRAND.muted};">${heading}</p><table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>${photoCell}<td valign="top"><p style="margin:0;font-family:${SANS};font-size:16px;line-height:22px;font-weight:bold;color:${BRAND.ink};">${lo.name}</p>${sub.length ? html`<p style="margin:2px 0 0;font-family:${SANS};font-size:13px;line-height:18px;color:${BRAND.muted};">${sub.join(' · ')}</p>` : ''}${lo.email ? line(emailLink(lo.email)) : ''}${lo.phone ? line(phoneLink(lo.phone)) : ''}</td></tr></table></td></tr></table>`;
}

/** Loan-officer row for a customer hero, from raw values. */
export function loFromRow(lo: { name: string; email?: string | null; roleLabel: string; photoUrl?: unknown }): Html {
  const email = lo.email ?? '';
  return trusted(loFromRowHtml({
    photoUrl: sanitizeWebUrlAttr(lo.photoUrl),
    loFull: escapeHtml(lo.name),
    loEmail: escapeHtml(email),
    loEmailHref: encodeURIComponent(email),
    roleLabel: escapeHtml(lo.roleLabel),
  }));
}

// ---------------------------------------------------------------------------
// Layouts
// ---------------------------------------------------------------------------

export interface FooterOptions {
  /** Lines after the address: a professional notice, a RESPA sentence, an unsubscribe link. */
  lines?: readonly HtmlValue[];
}

function emailShell(preheaderText: string, width: number, rows: string, footer: FooterOptions | undefined, fixedLayout = false): string {
  // Digests use a fixed table layout: the column stays the container's width, so a
  // wide data table scrolls inside its own frame instead of widening the page.
  const layout = fixedLayout ? 'table-layout:fixed;' : '';
  return `${preheader(preheaderText).__html}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
  <tr>
    <td align="center">
      <!--[if mso]><table role="presentation" width="${width}" align="center" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;${layout}max-width:${width}px;background:${BRAND.white};">${mastheadRowHtml()}${rows}${footerRowHtml(footer?.lines ?? [])}
      </table>
      <!--[if mso]></td></tr></table><![endif]-->
    </td>
  </tr>
</table>`;
}

export interface NoticeEmailInput {
  preheader: string;
  /** Plain strings are escaped; pass html`` for copy with apostrophes or markup. */
  title: HtmlValue;
  status?: StatusChip | null;
  /** One line under the chip, e.g. "Loan LW26011234 · Sarah Bennett". */
  meta?: string | null;
  /** The editable body slot: always the first element of the body. */
  body?: HtmlValue;
  /** Headline counts shown right after the body slot (digests). */
  tiles?: readonly StatTile[];
  blocks?: readonly HtmlValue[];
  cta?: { href: unknown; label: string } | null;
  note?: HtmlValue;
  footer?: FooterOptions;
}

function titledEmail(input: NoticeEmailInput, width: number, fixedLayout = false): string {
  const status = input.status ? chip(input.status) : '';
  const meta = input.meta
    ? html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:10px 0 0;"><tr><td style="font-family:${SANS};font-size:13px;line-height:19px;color:${BRAND.muted};">${input.meta}</td></tr></table>`
    : '';
  const body = renderHtml(input.body ?? null) ? bodySlot(input.body ?? null) : '';
  const tiles = input.tiles ? statTiles(input.tiles) : '';
  const cta = input.cta ? button({ href: input.cta.href, label: input.cta.label }) : '';
  const noteHtml = renderHtml(input.note ?? null) ? note(input.note ?? null) : '';
  const rows = html`
        <tr>
          <td bgcolor="${BRAND.paper}" style="background:${BRAND.paper};padding:22px 32px 20px;border-bottom:1px solid ${BRAND.rule};">
            <h1 style="margin:0;font-family:${SERIF};font-size:24px;line-height:30px;font-weight:normal;color:${BRAND.ink};">${input.title}</h1>${status}${meta}
          </td>
        </tr>
        <tr>
          <td style="padding:24px 32px 12px;">${body}${tiles}${input.blocks ?? []}${cta}${noteHtml}
          </td>
        </tr>`;
  return emailShell(input.preheader, width, rows.__html, input.footer, fixedLayout);
}

/** Internal and partner notices: paper title row (h1, chip, meta), white body, one action. */
export function noticeEmail(input: NoticeEmailInput): string {
  return titledEmail(input, BRAND_WIDTH.standard);
}

/**
 * Digests: the notice layout at 680px for tables of loans. Put headline counts
 * in `tiles` and each section (h2 heading, note, dataTable) in `blocks`.
 */
export function digestEmail(input: NoticeEmailInput): string {
  return titledEmail(input, BRAND_WIDTH.digest, true);
}

export interface CustomerEmailInput {
  preheader: string;
  hero: {
    /** Loan-officer row above the gold rule (the email reads as coming from a person). */
    from?: { name: string; email?: string | null; roleLabel: string; photoUrl?: unknown } | null;
    title: HtmlValue;
    status?: StatusChip | null;
    subhead?: HtmlValue;
    figure?: { label: string; value: string } | null;
    cta?: { href: unknown; label: string } | null;
  };
  greeting?: string | null;
  blocks?: readonly HtmlValue[];
  /** After the body: loan-officer card, sign-off. */
  closing?: readonly HtmlValue[];
  footer?: FooterOptions;
}

/** Borrowers, agents, partners and prospects: centered paper hero, then the letter. */
export function customerEmail(input: CustomerEmailInput): string {
  const from = input.hero.from ? html`${loFromRow(input.hero.from)}${trusted(goldRuleHtml())}` : '';
  const status = input.hero.status ? chip(input.hero.status, 'center') : '';
  const subhead = renderHtml(input.hero.subhead ?? null)
    ? html`<p style="margin:12px 0 0;font-family:${SANS};font-size:15px;line-height:23px;color:${BRAND.muted};text-align:center;">${input.hero.subhead ?? null}</p>`
    : '';
  const fig = input.hero.figure ? figure(input.hero.figure) : '';
  const cta = input.hero.cta
    ? html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:20px 0 0;"><tr><td align="center">${button({ href: input.hero.cta.href, label: input.hero.cta.label })}</td></tr></table>`
    : '';
  const greeting = input.greeting
    ? html`<p style="margin:0 0 14px;font-family:${SANS};font-size:16px;line-height:26px;color:${BRAND.bodyText};">${input.greeting}</p>`
    : '';
  const closing = input.closing && input.closing.length
    ? html`
        <tr>
          <td style="padding:0 34px 28px;">${input.closing}
          </td>
        </tr>`
    : '';
  const rows = html`
        <tr>
          <td align="center" bgcolor="${BRAND.paper}" style="background:${BRAND.paper};padding:24px 22px 24px;text-align:center;border-bottom:1px solid ${BRAND.rule};">${from}
            <h1 style="margin:0;font-family:${SERIF};font-size:28px;line-height:34px;font-weight:normal;color:${BRAND.ink};text-align:center;">${input.hero.title}</h1>${status}${subhead}${fig}${cta}
          </td>
        </tr>
        <tr>
          <td style="padding:28px 34px 8px;">${greeting}${input.blocks ?? []}
          </td>
        </tr>${closing}`;
  return emailShell(input.preheader, BRAND_WIDTH.standard, rows.__html, input.footer);
}
